import { supabase } from '../config/supabaseClient.js';
import { operadorAtual } from '../lib/operador.js';
import { sessao } from '../lib/auth.js';
import { formatarTitulo } from '../lib/dom.js';

/** Operador sugere CRIAR uma chave nova (vai p/ aprovação do gestor). */
export async function sugerirCriarChave(descricao, motivo) {
  const { error } = await supabase.from('chave_sugestao').insert({
    tipo: 'CRIAR', descricao: descricao ? formatarTitulo(descricao) : null, motivo: motivo || null,
    operador_usuario_id: operadorAtual.id || null, operador_nome: operadorAtual.nome || null,
  });
  return { erro: error ? error.message : null };
}

/** Operador sugere CANCELAR (inativar) uma chave existente. */
export async function sugerirCancelarChave(chaveId, motivo) {
  const { error } = await supabase.from('chave_sugestao').insert({
    tipo: 'CANCELAR', chave_id: chaveId, motivo: motivo || null,
    operador_usuario_id: operadorAtual.id || null, operador_nome: operadorAtual.nome || null,
  });
  return { erro: error ? error.message : null };
}

/** Lista sugestões PENDENTES (para o gestor decidir). */
export async function listarSugestoesPendentes() {
  const cols = 'id, tipo, chave_id, numero, descricao, motivo, sugerido_em, chave:chave_id ( numero, descricao )';
  const fazer = (sel) => supabase.from('chave_sugestao').select(sel).eq('status', 'PENDENTE').order('sugerido_em', { ascending: false });
  let { data, error } = await fazer(cols + ', operador_nome');
  if (error && (/operador_nome|column|does not exist|schema cache/i.test(error.message || '') || error.code === '42703' || error.code === 'PGRST204')) {
    // Migracao 0042 ainda nao rodada: lista sem o nome do operador (degrada, nao some a pendencia).
    ({ data, error } = await fazer(cols));
  }
  if (error) { console.error('[sugestoes]', error.message); return []; }
  return data || [];
}

/** Conta sugestões pendentes (badge do menu). */
export async function contarSugestoesPendentes() {
  const { count } = await supabase.from('chave_sugestao').select('*', { count: 'exact', head: true }).eq('status', 'PENDENTE');
  return count || 0;
}

/** Gestor APROVA: aplica a chave e marca a sugestão. */
export async function aprovarSugestao(s, descricaoFinal) {
  if (s.tipo === 'CRIAR') {
    const desc = (descricaoFinal != null ? descricaoFinal : s.descricao);
    const { error } = await supabase.from('chave').insert({ descricao: desc ? formatarTitulo(desc) : null, status: 'ATIVO' });
    if (error) return { erro: error.message };
  } else {
    const { error } = await supabase.from('chave').update({ status: 'INATIVO' }).eq('id', s.chave_id);
    if (error) return { erro: error.message };
  }
  const { error } = await supabase.from('chave_sugestao')
    .update({ status: 'APROVADA', decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() })
    .eq('id', s.id);
  return { erro: error ? error.message : null };
}

/** Gestor RECUSA a sugestão (com motivo opcional, visivel para quem sugeriu). */
export async function recusarSugestao(id, motivo) {
  const base = { status: 'RECUSADA', decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() };
  let { error } = await supabase.from('chave_sugestao').update({ ...base, recusa_motivo: motivo || null }).eq('id', id);
  if (error && (/recusa_motivo|column|schema cache/i.test(error.message || '') || error.code === 'PGRST204' || error.code === '42703')) {
    ({ error } = await supabase.from('chave_sugestao').update(base).eq('id', id));
  }
  return { erro: error ? error.message : null };
}


/* ===================== Sugestão de SETOR (recepção sugere; gestor aprova/cria) ===================== */

/** Recepção sugere um novo SETOR (vai p/ aprovação do gestor). */
export async function sugerirSetor(nome, motivo) {
  const { error } = await supabase.from('setor_sugestao').insert({
    nome: formatarTitulo(nome), motivo: motivo || null,
    operador_usuario_id: operadorAtual.id || null, operador_nome: operadorAtual.nome || null,
  });
  return { erro: error ? error.message : null };
}

/** Lista sugestões de setor PENDENTES. */
export async function listarSugestoesSetor() {
  const { data, error } = await supabase.from('setor_sugestao')
    .select('id, nome, motivo, operador_nome, sugerido_em')
    .eq('status', 'PENDENTE').order('sugerido_em', { ascending: false });
  if (error) { console.error('[setor_sugestao]', error.message); return []; }
  return data || [];
}

/** Conta sugestões de setor pendentes (badge do menu). */
export async function contarSugestoesSetor() {
  const { count } = await supabase.from('setor_sugestao').select('*', { count: 'exact', head: true }).eq('status', 'PENDENTE');
  return count || 0;
}

/** Gestor APROVA: cria o setor e marca a sugestão. */
export async function aprovarSugestaoSetor(s, nomeFinal) {
  const { error: e1 } = await supabase.from('setor').insert({ nome: formatarTitulo(nomeFinal != null ? nomeFinal : s.nome), status: 'ATIVO' });
  if (e1 && !/duplicate|unique/i.test(e1.message)) return { erro: e1.message };
  const { error } = await supabase.from('setor_sugestao')
    .update({ status: 'APROVADA', decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() })
    .eq('id', s.id);
  return { erro: error ? error.message : null };
}

/** Gestor FUNDE a sugestão de setor num setor JÁ EXISTENTE: marca APROVADA sem criar setor novo
 *  (a sugestão era duplicata de um setor cadastrado). Quem sugeriu vê como aprovada. */
export async function fundirSugestaoSetorExistente(id) {
  const { error } = await supabase.from('setor_sugestao')
    .update({ status: 'APROVADA', decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() })
    .eq('id', id);
  return { erro: error ? error.message : null };
}

/** Gestor RECUSA a sugestão de setor (com motivo opcional, visivel para quem sugeriu). */
export async function recusarSugestaoSetor(id, motivo) {
  const base = { status: 'RECUSADA', decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() };
  let { error } = await supabase.from('setor_sugestao').update({ ...base, recusa_motivo: motivo || null }).eq('id', id);
  if (error && (/recusa_motivo|column|schema cache/i.test(error.message || '') || error.code === 'PGRST204' || error.code === '42703')) {
    ({ error } = await supabase.from('setor_sugestao').update(base).eq('id', id));
  }
  return { erro: error ? error.message : null };
}


/* ===================== Sugestões do SISTEMA (feedback; gestor decide) ===================== */

/** Qualquer usuário envia uma sugestão de melhoria. */
export async function criarSugestaoSistema(texto) {
  const { error } = await supabase.from('sugestao_sistema').insert({
    texto: (texto || '').trim(),
    autor_usuario_id: operadorAtual.id || sessao.usuario?.id || null,
    autor_nome: operadorAtual.nome || null,
  });
  return { erro: error ? error.message : null };
}

/** Lista sugestões do sistema (status opcional: PENDENTE/IMPLEMENTADA/REJEITADA). */
export async function listarSugestoesSistema(status) {
  let q = supabase.from('sugestao_sistema')
    .select('id, texto, status, autor_nome, criado_em, decidido_em')
    .order('criado_em', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  if (error) { console.error('[sugestoes sistema]', error.message); return []; }
  return data || [];
}

/** Conta sugestões pendentes (badge). */
export async function contarSugestoesSistema() {
  const { count } = await supabase.from('sugestao_sistema').select('*', { count: 'exact', head: true }).eq('status', 'PENDENTE');
  return count || 0;
}

/** Gestor implementa ou rejeita (vai pro histórico). */
export async function decidirSugestaoSistema(id, novoStatus) {
  const { error } = await supabase.from('sugestao_sistema')
    .update({ status: novoStatus, decidido_por: sessao.usuario?.id || null, decidido_em: new Date().toISOString() })
    .eq('id', id);
  return { erro: error ? error.message : null };
}

/* ===================== Devolutiva de recusa ao operador ===================== */

/** Decisoes (aprovada/recusada) ainda nao vistas das sugestoes feitas por este operador. */
export async function feedbacksDecisao(operadorId) {
  if (!operadorId) return [];
  const DEC = ['APROVADA', 'RECUSADA'];
  const out = [];
  try {
    let { data, error } = await supabase.from('chave_sugestao')
      .select('id, tipo, status, descricao, recusa_motivo, chave:chave_id ( numero )')
      .eq('operador_usuario_id', operadorId).in('status', DEC).eq('recusa_vista', false);
    if (error) { ({ data } = await supabase.from('chave_sugestao')
      .select('id, tipo, status, descricao, chave:chave_id ( numero )')
      .eq('operador_usuario_id', operadorId).in('status', DEC).eq('recusa_vista', false)); }
    (data || []).forEach((x) => out.push({ kind: 'chave', id: x.id, status: x.status, motivo: x.recusa_motivo || '', texto: x.tipo === 'CRIAR' ? (x.descricao || 'nova chave') : ('cancelar chave ' + (x.chave ? x.chave.numero : '')) }));
  } catch (e) { /* ignora */ }
  try {
    let { data, error } = await supabase.from('setor_sugestao')
      .select('id, nome, status, recusa_motivo')
      .eq('operador_usuario_id', operadorId).in('status', DEC).eq('recusa_vista', false);
    if (error) { ({ data } = await supabase.from('setor_sugestao')
      .select('id, nome, status')
      .eq('operador_usuario_id', operadorId).in('status', DEC).eq('recusa_vista', false)); }
    (data || []).forEach((x) => out.push({ kind: 'setor', id: x.id, status: x.status, motivo: x.recusa_motivo || '', texto: x.nome }));
  } catch (e) { /* ignora */ }
  return out;
}

/** Marca as recusas como vistas (some na proxima vez). */
export async function marcarFeedbacksVistos(itens) {
  const chave = itens.filter((i) => i.kind === 'chave').map((i) => i.id);
  const setor = itens.filter((i) => i.kind === 'setor').map((i) => i.id);
  try { await supabase.rpc('marcar_recusa_vista', { p_chave: chave, p_setor: setor }); } catch (e) { /* ignore */ }
}
